import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { fetchEventPageData, fetchSignupState } from './data/eventPageApi'
import type { EventPageData, SignupState } from './data/types'
import { truthy } from './data/types'
import { sanitizeRichText } from './data/richText'
import { Header } from './components/Header'
import { Hero } from './components/Hero'
import { BroadcastBanner } from './components/BroadcastBanner'
import { ClassList } from './components/ClassList'
import { ClassesFooterBar } from './components/ClassesFooterBar'
import { unpaidEntrySummary } from './data/signupSummary'
import { ScheduleSection } from './components/ScheduleSection'
import { VenueSection } from './components/VenueSection'
import { RegistrationCard } from './components/RegistrationCard'
import { AmenitiesCard } from './components/AmenitiesCard'
import { Sponsors, Extras, RaceTimeCard, FeaturedEventsCard } from './components/SponsorsAndExtras'
import { RelatedEvents } from './components/RelatedEvents'
import { useIsMobile } from './hooks/useIsMobile'
import styles from './App.module.css'

type MobileTab = 'classes' | 'about' | 'schedule'

const MOBILE_TABS: Array<{ key: MobileTab; label: string }> = [
  { key: 'classes', label: 'Classes' },
  { key: 'about', label: 'About' },
  { key: 'schedule', label: 'Schedule & Venue' },
]

export default function App({ eventId }: { eventId: number }) {
  const [data, setData] = useState<EventPageData | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Classes is now the first tab (moved ahead of About) - defaulting to it too, so the tab bar's
  // highlighted tab matches what's actually showing on first load instead of landing on the
  // second tab with the first one unselected.
  const [mobileTab, setMobileTab] = useState<MobileTab>('classes')
  const [signup, setSignup] = useState<SignupState | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    let cancelled = false
    if (!eventId) {
      setError('No event id.')
      return
    }
    fetchEventPageData(eventId)
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((err: unknown) => {
        // Reported explicitly - this is caught (the page shows its own "Couldn't load this
        // event" screen below, not a crash), so Sentry's automatic GlobalHandlers never sees it.
        Sentry.captureException(err, { tags: { component: 'event-page-react' } })
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [eventId])

  // Racer-specific signup snapshot (vehicles, per-class entered/blocked/full state, extras) -
  // fetched separately from getEventPageData since most viewers of this page aren't a logged-in
  // racer. Re-fetched (via setSignup passed down as onSignupChange) after every successful
  // add - see the "Real class signup" plan for why the frontend replaces its whole snapshot
  // rather than hand-patching it.
  useEffect(() => {
    let cancelled = false
    if (!eventId || !data || data.viewer.type !== 'racer' || !truthy(data.viewer.loggedIn)) return
    fetchSignupState(eventId)
      .then((s) => {
        if (!cancelled) setSignup(s)
      })
      .catch((err: unknown) => {
        // Non-fatal - the page still works read-only if this fails; ClassList/Extras just fall
        // back to their anonymous/track-viewer rendering while signup stays null. Still worth
        // reporting though - it's a real failure (e.g. getSignupState errored server-side) that
        // silently degrades a logged-in racer's experience with no on-page indication at all.
        Sentry.captureException(err, { tags: { component: 'event-page-react' } })
      })
    return () => {
      cancelled = true
    }
  }, [eventId, data])

  if (error) {
    return (
      <div className={styles.stateScreen}>
        <p>Couldn't load this event.</p>
        <a href="/events/index.cfm">Back to events</a>
      </div>
    )
  }

  if (!data) {
    return <div className={styles.stateScreen}>Loading…</div>
  }

  const { classes, sponsors, extras, broadcasts, relatedEvents, raceTimeEvents, featuredEvents, viewer } = data
  const mobileUnpaid = unpaidEntrySummary(signup)
  const event = data.event
  // With a flyer image, its fixed height reliably makes the hero tall regardless of title
  // length, so a larger, more dramatic card overlap is safe. Without one, the hero's height is
  // governed entirely by the (variable-length) title text, so a smaller, padding-safe overlap is
  // used instead - see the "Compact" variants in App.module.css.
  const hasFlyer = Boolean(event.flyerUrl)

  // event.description is organizer-authored rich text (Quill/CKEditor, per CLAUDE.md) - the
  // legacy page outputs it unescaped (`#qGetEvent.description#` directly in HTML), so it already
  // contains real markup, not plain text. Matching that same trust boundary here (organizer/track
  // accounts only, not public input) rather than escaping it into literal "<p>...</p>" text.
  const aboutSection = event.description ? (
    <section id="about" className={styles.section}>
      <h2 className={styles.heading}>About the race</h2>
      {/* eslint-disable-next-line react/no-danger */}
      <div className={styles.about} dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.description) }} />
    </section>
  ) : null

  return (
    <div className={styles.page}>
      <Header viewer={viewer} />
      <Hero event={event} />

      {/* Mobile: the registration card pulls up to overlap the hero's bottom edge and sits
          above the tab bar - visible on every tab, not just About (Main.dc.html: the card
          sits between the title/flyer hero and the section nav, `margin: -56px 16px 0`). */}
      {/* MobileVenue.dc.html keeps amenities inside the Venue section itself, as bare checkmark
          chips VenueSection renders directly (isMobile) - not a standalone AmenitiesCard like
          desktop, where Desktop.dc.html's aside is reg card -> amenities card -> sponsor slot.
          Only the desktop rail gets AmenitiesCard as a standalone card; see below. */}
      {isMobile ? (
        <div className={`${styles.mobileRegWrap} ${hasFlyer ? '' : styles.mobileRegWrapCompact}`}>
          <RegistrationCard event={event} classes={classes} extras={extras} signup={signup} isMobile onGotoClasses={() => setMobileTab('classes')} />
        </div>
      ) : null}

      <div className={styles.body}>
        {broadcasts.length ? (
          <div className={styles.bannerWrap}>
            <BroadcastBanner broadcasts={broadcasts} />
          </div>
        ) : null}

        {isMobile ? (
          <>
            <nav aria-label="Event sections" className={styles.tabBar}>
              {MOBILE_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`${styles.tab} ${mobileTab === t.key ? styles.tabActive : ''}`}
                  onClick={() => setMobileTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            <main className={styles.main}>
              {mobileTab === 'classes' ? (
                <div className={styles.classesTabGroup} style={mobileUnpaid.count > 0 ? { paddingBottom: 96 } : undefined}>
                  <section className={styles.section}>
                    <h2 className={styles.heading}>Classes</h2>
                    <ClassList classes={classes} event={event} viewer={viewer} signup={signup} isMobile onSignupChange={setSignup} />
                  </section>
                  <Extras extras={extras} dollarType={event.dollarType} viewer={viewer} signup={signup} onSignupChange={setSignup} eventId={event.id} />
                </div>
              ) : null}
              {mobileTab === 'about' ? (
                <>
                  <Sponsors sponsors={sponsors} id="sponsors" />
                  {aboutSection}
                </>
              ) : null}
              {mobileTab === 'schedule' ? (
                <>
                  <section className={styles.section}>
                    <h2 className={styles.heading}>Race week</h2>
                    <ScheduleSection event={event} />
                  </section>
                  <section className={styles.section}>
                    <h2 className={styles.heading}>Venue &amp; hotel</h2>
                    <VenueSection event={event} isMobile />
                  </section>
                </>
              ) : null}
            </main>
            {/* MobileClasses.dc.html's sticky bottom bar - only on the Classes tab, only once
                there's something unpaid to "Continue" to. Desktop has no equivalent: its
                RegistrationCard rail already stays visible while scrolling. */}
            {mobileTab === 'classes' && signup ? <ClassesFooterBar signup={signup} dollarType={event.dollarType} /> : null}
          </>
        ) : (
          <div className={styles.desktopGrid}>
            <main className={styles.main}>
              <nav aria-label="Event sections" className={styles.anchorNav}>
                <a href="#classes">Classes</a>
                <a href="#schedule">Schedule</a>
                {/* Sponsors sits directly above About with no nav entry of its own (it's
                    conditional - not every event has sponsors) - when there are any, "About"
                    jumps to the top of that section instead, so scrolling past it into About is
                    the natural next step rather than skipping over it entirely. */}
                <a href={sponsors.length ? '#sponsors' : '#about'}>About</a>
                <a href="#venue">Venue &amp; hotel</a>
              </nav>

              <section id="classes" className={styles.section}>
                <h2 className={styles.heading}>Classes</h2>
                <ClassList classes={classes} event={event} viewer={viewer} signup={signup} isMobile={false} onSignupChange={setSignup} />
              </section>

              <Extras extras={extras} dollarType={event.dollarType} viewer={viewer} signup={signup} onSignupChange={setSignup} eventId={event.id} />

              <section id="schedule" className={styles.section}>
                <h2 className={styles.heading}>Race week</h2>
                <ScheduleSection event={event} />
              </section>

              <Sponsors sponsors={sponsors} id="sponsors" />
              {aboutSection}

              <section id="venue" className={styles.section}>
                <h2 className={styles.heading}>Venue &amp; hotel</h2>
                <VenueSection event={event} isMobile={false} />
              </section>

              <RelatedEvents events={relatedEvents} />
            </main>

            <aside className={`${styles.rail} ${hasFlyer ? '' : styles.railCompact}`}>
              <RegistrationCard
                event={event}
                classes={classes}
                extras={extras}
                signup={signup}
                isMobile={false}
                onGotoClasses={() => document.getElementById('classes')?.scrollIntoView({ behavior: 'smooth' })}
              />
              <AmenitiesCard amenities={event.trackAmenities} />
              <RaceTimeCard events={raceTimeEvents} />
              <FeaturedEventsCard events={featuredEvents} />
            </aside>
          </div>
        )}

        {isMobile ? (
          <>
            <RelatedEvents events={relatedEvents} />
            <RaceTimeCard events={raceTimeEvents} />
            <FeaturedEventsCard events={featuredEvents} />
          </>
        ) : null}
      </div>
    </div>
  )
}
